import { cn } from "@/shared/lib/utils";
import Text from "@/shared/ui/refresh-components/texts/Text";
import Truncated from "@/shared/ui/refresh-components/texts/Truncated";
import { WithoutStyles } from "@/shared/types";
import { Content } from "@opal/layouts";
import { IconProps } from "@opal/types";
import React from "react";

export type FlexDirection = "row" | "column";
export type JustifyContent = "start" | "center" | "end" | "between";
export type AlignItems = "start" | "center" | "end" | "stretch";
export type Length = "auto" | "fit" | "full" | number;

const flexDirectionClassMap: Record<FlexDirection, string> = {
  row: "flex-row",
  column: "flex-col",
};
const justifyClassMap: Record<JustifyContent, string> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
};
const alignClassMap: Record<AlignItems, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
};
export const widthClassmap: Record<Length, string> = {
  auto: "w-auto flex-shrink-0",
  fit: "w-fit flex-shrink-0",
  full: "w-full",
};
export const heightClassmap: Record<Length, string> = {
  auto: "h-auto",
  fit: "h-fit",
  full: "h-full min-h-0",
};

/**
 * Section - A flexible container component for grouping related content
 *
 * Provides a standardized layout container with configurable direction and spacing.
 * Uses flexbox layout with customizable gap between children. Defaults to column layout.
 *
 * @param flexDirection - Flex direction. Default: "column".
 * @param justifyContent - Justify content along the main axis. Default: "center".
 * @param alignItems - Align items along the cross axis. Default: "center".
 * @param width - Width of the container: "auto", "fit", or "full". Default: "full".
 * @param height - Height of the container: "auto", "fit", or "full". Default: "full".
 * @param gap - Gap in REM units between children. Default: 1 (translates to gap-4 in Tailwind)
 * @param padding - Padding in REM units. Default: 0
 * @param wrap - If true, enables flex-wrap. Default: false
 * @param dbg - If true, adds a debug red border for visual debugging. Default: false
 *
 * @example
 * ```tsx
 * import * as GeneralLayouts from "@/shared/ui/layouts/general-layouts";
 *
 * // Column section with default gap - centered
 * <GeneralLayouts.Section>
 *   <Card>First item</Card>
 *   <Card>Second item</Card>
 * </GeneralLayouts.Section>
 *
 * // Row section aligned to the left and vertically centered
 * <GeneralLayouts.Section flexDirection="row" justifyContent="start" alignItems="center">
 *   <Button>Cancel</Button>
 *   <Button>Save</Button>
 * </GeneralLayouts.Section>
 *
 * // Column section with items aligned to the right
 * <GeneralLayouts.Section alignItems="end" gap={2}>
 *   <InputTypeIn label="Name" />
 *   <InputTypeIn label="Email" />
 * </GeneralLayouts.Section>
 *
 * // Row section centered both ways
 * <GeneralLayouts.Section flexDirection="row" justifyContent="center" alignItems="center">
 *   <Text>Centered content</Text>
 * </GeneralLayouts.Section>
 *
 * // Section with fit width
 * <GeneralLayouts.Section width="fit">
 *   <Button>Fit to content</Button>
 * </GeneralLayouts.Section>
 * ```
 *
 * @remarks
 * - The component defaults to column layout when no direction is specified
 * - Full width and height by default
 * - Accepts className for additional styling; style prop is not available
 * - Import using namespace import for consistent usage: `import * as GeneralLayouts from "@/shared/ui/layouts/general-layouts"`
 */
export interface SectionProps
  extends WithoutStyles<React.HtmlHTMLAttributes<HTMLDivElement>> {
  className?: string;
  flexDirection?: FlexDirection;
  justifyContent?: JustifyContent;
  alignItems?: AlignItems;
  width?: Length;
  height?: Length;

  gap?: number;
  padding?: number;
  wrap?: boolean;

  // Debugging utilities
  dbg?: boolean;

  ref?: React.Ref<HTMLDivElement>;
}

/**
 * `<Disabled>` from `@opal/core` uses `display: contents` — it can safely
 * wrap a `Section` without affecting layout.
 */
function Section({
  className,
  flexDirection = "column",
  justifyContent = "center",
  alignItems = "start",
  width = "full",
  height = "full",
  gap = 1,
  padding = 0,
  wrap,
  dbg,
  ref,
  ...rest
}: SectionProps) {
  return (
    <div
      ref={ref}
      className={cn(
        "flex",

        flexDirectionClassMap[flexDirection],
        justifyClassMap[justifyContent],
        alignClassMap[alignItems],
        typeof width === "string" && widthClassmap[width],
        typeof height === "string" && heightClassmap[height],
        typeof height === "number" && "overflow-hidden",

        wrap && "flex-wrap",
        dbg && "dbg-red",
        className
      )}
      style={{
        gap: `${gap}rem`,
        padding: `${padding}rem`,
        ...(typeof width === "number" && { width: `${width}rem` }),
        ...(typeof height === "number" && { height: `${height}rem` }),
      }}
      {...rest}
    />
  );
}

export interface AttachmentItemLayoutProps {
  title: string;
  description: string;
  icon: React.FunctionComponent<IconProps>;
  middleText?: string;
  rightChildren?: React.ReactNode;
}
function AttachmentItemLayout({
  title,
  description,
  icon: Icon,
  middleText,
  rightChildren,
}: AttachmentItemLayoutProps) {
  return (
    <Section
      flexDirection="row"
      justifyContent="start"
      gap={0.25}
      padding={0.25}
    >
      <div className={cn("h-[2.25rem] aspect-square rounded-08 flex-shrink-0")}>
        <Section>
          <div
            className="attachment-button__icon-wrapper"
            data-testid="attachment-item-icon-wrapper"
          >
            <Icon className="attachment-button__icon" />
          </div>
        </Section>
      </div>
      <Section
        flexDirection="row"
        justifyContent="between"
        alignItems="center"
        gap={1.5}
        className="min-w-0"
      >
        <div data-testid="attachment-item-title" className="flex-1 min-w-0">
          <Content
            title={title}
            description={description}
            sizePreset="main-ui"
            variant="section"
            width="full"
          />
        </div>
        {middleText && (
          <div className="flex-1 min-w-0">
            <Truncated text03 secondaryBody>
              {middleText}
            </Truncated>
          </div>
        )}
        {rightChildren && (
          <div className="flex-shrink-0 px-1">{rightChildren}</div>
        )}
      </Section>
    </Section>
  );
}

/**
 * CardItemLayout - A layout for card headers with icon, title, description, and actions
 *
 * Structure:
 *   Column [
 *     Row [
 *       Row [ Icon (18px), Title ],
 *       rightChildren (action buttons)
 *     ],
 *     Description (optional, 2-line clamp)
 *   ]
 *
 * Used for card components that display an entity with:
 * - An icon on the left (18px, controlled by this layout)
 * - A title next to the icon
 * - Optional action buttons on the right
 * - Optional description below (2-line max)
 *
 * @param icon - Icon component to render on the left. Receives `size` prop from layout.
 *               Use a callback for custom components: `(props) => <AgentAvatar {...props} />`
 * @param title - The main title text
 * @param description - Optional description text below the title row (clamped to 2 lines)
 * @param rightChildren - Optional content on the right (typically action buttons)
 */
export interface CardItemLayoutProps {
  icon: React.FunctionComponent<IconProps>;
  title: string;
  description?: string;
  rightChildren?: React.ReactNode;
}
function CardItemLayout({
  icon: Icon,
  title,
  description,
  rightChildren,
}: CardItemLayoutProps) {
  return (
    <div className="flex flex-col flex-1 self-stretch items-center gap-1 p-1">
      <div className="flex flex-row self-stretch items-center justify-between gap-1">
        <div className="flex flex-row items-center self-stretch p-1.5 gap-1.5">
          <div className="px-0.5">
            <Icon size={18} />
          </div>
          <Truncated mainContentBody>{title}</Truncated>
        </div>

        {rightChildren && (
          <div className={cn("flex flex-row p-0.5 items-center")}>
            {rightChildren}
          </div>
        )}
      </div>

      {description && (
        <div className="pb-1 px-2 flex self-stretch">
          <Text
            as="p"
            secondaryBody
            text03
            className="line-clamp-2 truncate whitespace-normal h-[2.2rem] break-words"
          >
            {description}
          </Text>
        </div>
      )}
    </div>
  );
}
export { Section, CardItemLayout, AttachmentItemLayout };
